import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { X } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import type { EvidenceImage } from '../../contracts';
import { evidenceImageBlockMessages } from './evidenceImageBlock.messages';

function getHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

interface ImageCaptionProps {
  readonly image: EvidenceImage;
  readonly variant?: 'inline' | 'lightbox';
}

function ImageCaption({ image, variant = 'inline' }: ImageCaptionProps) {
  const hostname = getHostname(image.url);
  const description = image.description?.trim();
  const linkClass =
    variant === 'lightbox'
      ? 'underline underline-offset-2 hover:text-white'
      : 'underline underline-offset-2 hover:text-text-primary';

  const source = (
    <a
      href={image.url}
      target="_blank"
      rel="noopener noreferrer"
      className={linkClass}
      onClick={(e) => e.stopPropagation()}
    >
      {hostname}
    </a>
  );

  const values = {
    n: image.n,
    source,
    imageLabel: (chunks: ReactNode) => <span className="font-medium">{chunks}</span>,
  };

  if (description) {
    return (
      <FormattedMessage
        {...evidenceImageBlockMessages.captionWithDescription}
        values={{ ...values, description }}
      />
    );
  }

  return (
    <FormattedMessage {...evidenceImageBlockMessages.captionSourceOnly} values={values} />
  );
}

export function ImageLightbox({
  image,
  onClose,
}: {
  readonly image: EvidenceImage;
  readonly onClose: () => void;
}) {
  const intl = useIntl();
  const [hidden, setHidden] = useState(false);

  if (hidden) return null;

  const fallbackAlt = intl.formatMessage(evidenceImageBlockMessages.fallbackAlt, {
    n: image.n,
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 duration-150 animate-in fade-in-0">
      <button
        type="button"
        className="absolute inset-0 size-full cursor-default border-0 bg-transparent"
        onClick={onClose}
        aria-label={intl.formatMessage(evidenceImageBlockMessages.closePreview)}
      />

      <button
        type="button"
        className="absolute end-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
        onClick={onClose}
        aria-label={intl.formatMessage(evidenceImageBlockMessages.close)}
      >
        <X className="size-5" />
      </button>

      <div className="pointer-events-none relative z-10 flex max-h-[90vh] max-w-[90vw] flex-col items-center">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={image.url}
          alt={image.description || fallbackAlt}
          className="pointer-events-auto max-h-[85vh] max-w-[90vw] rounded-lg object-contain"
          referrerPolicy="no-referrer"
          onError={() => setHidden(true)}
        />
        <TypographyP2 className="pointer-events-auto mt-3 max-w-xl text-center text-white/80">
          <ImageCaption image={image} variant="lightbox" />
        </TypographyP2>
      </div>
    </div>
  );
}

interface EvidenceImageBlockProps {
  readonly image: EvidenceImage;
}

export function EvidenceImageBlock({ image }: EvidenceImageBlockProps) {
  const intl = useIntl();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  const openLightbox = useCallback(() => setLightboxOpen(true), []);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeLightbox();
    };
    // Capture + preventDefault claims Esc, so page-wide Esc bindings (e.g. stop generating) stand down.
    document.addEventListener('keydown', handleKey, { capture: true });
    return () => document.removeEventListener('keydown', handleKey, { capture: true });
  }, [lightboxOpen, closeLightbox]);

  if (hidden) return null;

  const fallbackImageAlt = intl.formatMessage(evidenceImageBlockMessages.fallbackAlt, {
    n: image.n,
  });
  const buttonLabel = image.description
    ? intl.formatMessage(evidenceImageBlockMessages.viewFullWithDescription, {
        description: image.description,
      })
    : intl.formatMessage(evidenceImageBlockMessages.viewFullByNumber, { n: image.n });

  return (
    <>
      <figure className="my-3">
        <button
          type="button"
          className="group w-full cursor-pointer border-0 bg-transparent p-0 text-left"
          onClick={openLightbox}
          aria-label={buttonLabel}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={image.url}
            alt={image.description || fallbackImageAlt}
            className="max-h-80 w-full rounded-lg object-contain transition-opacity group-hover:opacity-80"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setHidden(true)}
          />
        </button>
        <TypographyCaption className="mt-1 text-text-secondary">
          <ImageCaption image={image} />
        </TypographyCaption>
      </figure>

      {lightboxOpen &&
        createPortal(
          <ImageLightbox image={image} onClose={closeLightbox} />,
          document.body
        )}
    </>
  );
}
